import { FormControl, Icon, InputLabel, MenuItem, Typography } from '@mui/material';
import { FormikProvider } from 'formik';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import DateRangeInput from '@/components/common/Inputs/DateRangeInput/DateRangeInput';
import { FQ_BUTTON_VARIANTS } from '@/components/muiCustomized/FQButton/constant';
import FQButton from '@/components/muiCustomized/FQButton/FQButton';
import FQSelect, { FQSELECT_VARIANTS } from '@/components/muiCustomized/FQSelect/FQSelect';
import FQTextField, { FQTEXT_FIELD_VARIANTS } from '@/components/muiCustomized/FQTextField/FQTextField';
import { AbsenceWithTypeData } from '@/domain/requestsPage';
import { MEDIA_QUERIES_BREAKPOINTS } from '@/globalStyles';
import Calendar from '@/pages/NewRequest/Calendar/Calendar';
import Summary from '@/pages/NewRequest/Summary/Summary';
import useEditRequestLogic, { UPDATE_REQUEST_FIELD } from '@/pages/Requests/common/EditRequest/useEditRequestLogic';

interface IEditRequestProps {
    className?: string;
    onCancel: () => void;
    singleRequestDetails: AbsenceWithTypeData;
    onCloseModal: () => void;
    getRequests: () => void;
}

const EditRequest = ({ singleRequestDetails, onCancel, onCloseModal, getRequests, className }: IEditRequestProps) => {
    const { t } = useTranslation();

    const { limitsUsage, formik, requestSummary, isLimitExceeded, isLoading } = useEditRequestLogic({
        singleRequestDetails,
        onCloseModal,
        getRequests,
    });

    const selectOptions = useMemo(
        () =>
            limitsUsage?.map(limitUsage => {
                const { _id, name, color, icon } = limitUsage?.absenceType || {};

                return (
                    <MenuItem key={_id} value={_id}>
                        <Icon sx={{ color, marginRight: '0.5rem' }}>{icon}</Icon>
                        {name}
                    </MenuItem>
                );
            }),
        [limitsUsage, t]
    );

    return (
        <div className={className}>
            <FormikProvider value={formik}>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">{t('requestDetails_requestFor_title')}</InputLabel>
                    <FQSelect
                        name={UPDATE_REQUEST_FIELD.selectedRequestTypeId}
                        data-variant={FQSELECT_VARIANTS.fqOutlined}
                        value={formik.values[UPDATE_REQUEST_FIELD.selectedRequestTypeId] || ''}
                        onChange={formik.handleChange}
                        label={t('requestDetails_requestFor_title')}
                        disabled={isLoading}
                    >
                        {selectOptions}
                    </FQSelect>
                </FormControl>

                <DateRangeInput
                    name={UPDATE_REQUEST_FIELD.dateRange}
                    onChange={formik.setFieldValue}
                    dateRange={formik.values.dateRange}
                    disabled={isLoading}
                    error={isLimitExceeded}
                />

                <Calendar
                    isLimitExceeded={isLimitExceeded}
                    disabled={isLoading}
                    dateRange={formik.values.dateRange}
                    onChange={formik.setFieldValue}
                    error={formik.errors?.dateRange as string}
                    touched={Boolean(formik.touched?.dateRange)}
                    userId={singleRequestDetails.userId}
                    hideOverlappingError
                />

                <Summary requestSummary={requestSummary} />

                <FQTextField
                    variant="outlined"
                    data-variant={FQTEXT_FIELD_VARIANTS.fqTextArea}
                    disabled={isLoading}
                    name={UPDATE_REQUEST_FIELD.comment}
                    value={formik.values?.comment}
                    onChange={formik.handleChange}
                    placeholder={t('newApplication_applicationComment_placeholder')}
                    multiline
                    minRows={3}
                    maxRows={3}
                    fullWidth
                    onBlur={formik.handleBlur}
                />

                <ButtonsWrapper>
                    <FQButton data-variant={FQ_BUTTON_VARIANTS.fqOutline} color="fqRed" onClick={onCancel}>
                        <Typography variant="button">{t('alert_defaultDeclineAction_title')}</Typography>
                    </FQButton>

                    <FQButton
                        disabled={isLimitExceeded || isLoading}
                        data-variant={FQ_BUTTON_VARIANTS.fqOutline}
                        color="fqGreen"
                        type="submit"
                        onClick={() => formik.handleSubmit()}
                    >
                        {t('imagePicker_saveButton_title')}
                    </FQButton>
                </ButtonsWrapper>
            </FormikProvider>
        </div>
    );
};

const ButtonsWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.5rem;

    @media screen and (width < ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
        justify-content: center;
    }
`;

const Memoized = memo(EditRequest);
const Styled = styled(Memoized)`
    .MuiSelect-select.MuiInputBase-input {
        display: flex;
        align-items: center;
    }

    & > div {
        margin-bottom: 1.25rem;
    }

    .rdrMonth {
        @media screen and (width <= ${MEDIA_QUERIES_BREAKPOINTS.stablet}px) {
            scale: 0.8;
        }
    }
`;

export default Styled;
